import { useCallback, useContext, useEffect, useState } from "react";
import { AppContext } from "../State/Context";
import { BasicPopup } from "./BasicPopup";
import { ActionType } from "../State/State";
import { getWorldCoordsToCenterHex, getCenterHexFromWorld } from "../State/stateUtils";
import { askAPI, RequestMethod } from "../api/api";
import { imgFromCoords } from "../utils/settings";
import { HiMagnifyingGlass } from "react-icons/hi2";
import { Icon } from "../components/Icon";

export type SearchResultItem = {
  x: number;
  y: number;
  headline: string;
  prompt: string;
};

const SEARCH_LIMIT = 128;
const HEX_SIZE = 128;
const COLS = 4;
const GRID_GAP = 8;
const FORM_WIDTH = COLS * HEX_SIZE + (COLS - 1) * GRID_GAP;

export const SearchPopup: React.FC = () => {
  const { state, dispatch } = useContext(AppContext);
  const closePopup = useCallback(() => {
    if (typeof window !== "undefined" && window.location.pathname === "/search") {
      const center = getCenterHexFromWorld(
        state.worldX,
        state.worldY,
        state.worldScale,
        state.hexGridBuffer ?? 0
      );
      const path = center ? `/hex/${center.x}/${center.y}` : "/";
      window.history.replaceState(null, "", path);
    }
    dispatch({ type: ActionType.TOGGLE_SEARCH_POPUP });
  }, [dispatch, state.worldX, state.worldY, state.worldScale, state.hexGridBuffer]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      closePopup();
    };
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [closePopup]);

  const [inputValue, setInputValue] = useState("");
  const [results, setResults] = useState<SearchResultItem[]>([]);
  const [loading, setLoading] = useState(false);

  const runSearch = useCallback(async (q: string) => {
    if (!q.trim()) {
      setResults([]);
      return;
    }
    setLoading(true);
    const data = await askAPI<SearchResultItem[]>(
      `/api/hexagen/search?q=${encodeURIComponent(q.trim())}&limit=${SEARCH_LIMIT}`,
      RequestMethod.GET
    );
    setResults(Array.isArray(data) ? data : []);
    setLoading(false);
    if (typeof window !== "undefined") {
      try {
        if ((window as any).gtag) {
          (window as any).gtag("event", "view_search_results", { search_term: q.trim() });
        }
        if ((window as any).rdt) {
          (window as any).rdt("track", "Search", { searchString: q.trim() });
        }
      } catch (_) {}
    }
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    runSearch(inputValue);
  };

  const onClickHex = (x: number, y: number) => {
    dispatch({ type: ActionType.SELECT_HEX, x, y });
    dispatch({ type: ActionType.OPEN_HEX_INFO_POPUP });
    dispatch({ type: ActionType.TOGGLE_SEARCH_POPUP });
    const coords = getWorldCoordsToCenterHex(x, y, state.worldScale);
    dispatch({ type: ActionType.CHANGE_WORLD_COORDINATES, x: coords.worldX, y: coords.worldY });
  };

  return (
    <BasicPopup onClose={closePopup}>
      <div className="overflow-x-hidden min-w-0">
      <div className="font-bold text-lg mb-3 min-w-0">Search hexagens</div>
      <div className="min-w-0 max-w-full" style={{ width: FORM_WIDTH }}>
        <form
          onSubmit={handleSubmit}
          className="mb-4 flex gap-2 items-stretch w-full min-w-0"
        >
          <div className="relative flex-1 min-w-0 overflow-hidden">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Search by prompt or title…"
              className="w-full h-full min-h-[40px] px-3 py-2 pl-9 bg-white border border-gray-300 rounded text-black placeholder-gray-500 focus:outline-none focus:border-black"
              autoFocus
            />
            <Icon
              icon={HiMagnifyingGlass}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 text-base pointer-events-none"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 h-[40px] bg-black text-white rounded transition-colors font-medium shrink-0 flex items-center justify-center"
          >
            Search
          </button>
        </form>
      </div>
      {loading && <p className="text-sm opacity-60 mb-2">Loading…</p>}
      {!loading && inputValue.trim() && results.length === 0 && (
        <p className="text-sm opacity-60 mb-2">No hexagens found for &quot;{inputValue.trim()}&quot;.</p>
      )}
      {!loading && results.length > 0 && (
        <p className="text-sm opacity-60 mb-2">
          {results.length} result{results.length !== 1 ? "s" : ""} (max {SEARCH_LIMIT})
        </p>
      )}
      <div className="overflow-y-auto overflow-x-hidden max-h-[min(60vh,400px)] pr-1 min-w-0" style={{ width: FORM_WIDTH, maxWidth: "100%" }}>
        <div
          className="grid min-w-0"
          style={{
            gridTemplateColumns: `repeat(${COLS}, ${HEX_SIZE}px)`,
            gap: GRID_GAP,
          }}
        >
          {results.map((h) => (
            <button
              key={`${h.x}-${h.y}`}
              type="button"
              onClick={() => onClickHex(h.x, h.y)}
              title={h.headline || h.prompt || `(${h.x}, ${h.y})`}
              className="w-[128px] h-[128px] rounded overflow-hidden border-none focus:outline-none p-0 block bg-transparent hover:bg-transparent focus:bg-transparent transition-transform duration-150 hover:scale-105 focus:scale-105"
            >
              <img
                src={imgFromCoords(h.x, h.y)}
                alt={`${h.x}, ${h.y}`}
                className="w-full h-full object-cover block"
                width={HEX_SIZE}
                height={HEX_SIZE}
              />
            </button>
          ))}
        </div>
      </div>
      </div>
    </BasicPopup>
  );
};
